import { arrayFlat, arrayInsert, arrayUnique } from "common/utilities/core/array";
import { findRangeIntersection, generateRandomNumber } from "common/utilities/core/number";
import type {
  EntitySequence as Sequence,
  SequenceAlign,
  SequenceFeature,
  SequenceType,
} from "sequenceAnalyser/api/sequenceAnalyserApiClient";
import { LABEL_COMPONENT } from "sequenceAnalyser/global/settings.ts";
import type {
  SequenceSelection,
  SequenceSelectionWithSequenceData,
} from "sequenceAnalyser/utilities/selection/selection";
import type { FeatureDefinitions, RemovedSequence } from "sequenceAnalyser/utilities/sequenceAnalyser.types";
import {
  buildFeatureId,
  buildFeatureName,
  getSequenceLabel,
  residuesStringToArray,
} from "sequenceAnalyser/utilities/sequenceAnalyser/sequenceAnalyser";

import { isSelectionGapsOnly, removeAllGaps, removeGapsFromPosition } from "./removeGaps";

export enum SequenceQualifiedIdTypes {
  SessionStore = "STO",
  ManuallyInserted = "SEQ",
  Consensus = "CONS",
}

export type SequenceQualifiedIdType = `${SequenceQualifiedIdTypes}`;

export function selectedSequences(sequences: Sequence[], selection: SequenceSelection) {
  return sequences.filter((s) => selection.ids.includes(s.id));
}

export function selectedAlignedSequences(sequences: SequenceAlign[], selection: SequenceSelection) {
  return sequences.filter((sequence) => selection.ids.includes(sequence.id));
}

export function highestQualifiedIdForManuallyInsertedSequences(
  sequenceQualifiedIdType: SequenceQualifiedIdType,
  sequences: Sequence[],
) {
  const ids = sequences
    .filter((s) => s.qualifiedId.match(`${sequenceQualifiedIdType}-`))
    .map((s) => Number(s.qualifiedId.replace(`${sequenceQualifiedIdType}-`, "")));

  return ids.length === 0 ? 0 : Math.max(...ids);
}

export function generateSequenceQualifiedId(
  sequenceQualifiedIdType: SequenceQualifiedIdType,
  sequences: Sequence[],
  idNumber?: number,
) {
  return [
    sequenceQualifiedIdType,
    idNumber === undefined
      ? highestQualifiedIdForManuallyInsertedSequences(sequenceQualifiedIdType, sequences) + 1
      : idNumber,
  ].join("-");
}
/**
 * Remove sequences present in the selection from a given sequences list
 */
export function removeSequences(sequences: SequenceAlign[], selection: SequenceSelection) {
  return sequences.reduce<{
    remainingSequences: SequenceAlign[];
    removedSequences: RemovedSequence[];
  }>(
    (acc, sequence, position) => {
      if (selection.ids.includes(sequence.id)) {
        acc.removedSequences.push({ sequence, position });
      } else {
        acc.remainingSequences.push(sequence);
      }
      return acc;
    },
    { remainingSequences: [], removedSequences: [] },
  );
}

export function residuesSelection(residues: string, positionStart?: number, positionEnd?: number) {
  const residuesAsArray = residuesStringToArray(residues);

  if (positionStart !== undefined && positionEnd !== undefined) {
    return residuesAsArray.slice(positionStart, positionEnd + 1).join("");
  }
  return residuesAsArray.join("");
}

export function sequenceSelectionAsString(alignedSequences: SequenceAlign[], selection: SequenceSelection) {
  const str = selectedAlignedSequences(alignedSequences, selection)
    .map((alignedSequence) =>
      residuesSelection(alignedSequence.residues, selection.positionStart, selection.positionEnd),
    )
    .join("\n");

  return str;
}
export function sequenceSelectionIDsAsString(sequences: Sequence[], selection: SequenceSelection) {
  const filteredSelectedSequences = selectedSequences(sequences, selection).filter((s) => s.entityQualifiedId);
  const uniqueIds = arrayUnique(filteredSelectedSequences.map((sequence) => sequence.entityQualifiedId));
  const str = uniqueIds.join("\n");

  return { str, count: uniqueIds.length };
}

export function sequenceSelectionAsFastaString(
  sequences: Sequence[],
  alignedSequences: SequenceAlign[],
  selection: SequenceSelection,
  labelComposition: string[],
) {
  const selectionPositionAsString =
    selection.positionStart !== undefined && selection.positionEnd !== undefined
      ? ` (${selection.positionStart}-${selection.positionEnd})`
      : "";

  const str = selectedAlignedSequences(alignedSequences, selection)
    .map((alignedSequence) => {
      const sequence = sequences.find((s) => s.id === alignedSequence.id) as Sequence;

      return [
        `>${getSequenceLabel(sequence, labelComposition, true)}${selectionPositionAsString}`,
        residuesSelection(alignedSequence.residues, selection.positionStart, selection.positionEnd),
      ].join("\n");
    })
    .join("\n");

  return str;
}

/** Adjust the selection position of an aligned sequence to non-aligned (raw) sequence */
export function alignedSelectionToNonAlignedSelection(selection: SequenceSelection, sequence: SequenceAlign) {
  if (selection.positionStart === undefined || selection.positionEnd === undefined) return undefined;

  const residuesTilPositionStart = removeGapsFromPosition(sequence.residues, 0, selection.positionStart);
  const residuesTilPositionEnd = removeGapsFromPosition(sequence.residues, 0, selection.positionEnd);

  return {
    selectionPositionStart: selection.positionStart - (sequence.residues.length - residuesTilPositionStart.length),
    selectionPositionEnd: selection.positionEnd - (sequence.residues.length - residuesTilPositionEnd.length),
  };
}

export function sequenceFeaturesSelectionAsFastaString(
  content: "aligned" | "raw",
  rawSequences: Sequence[],
  sequences: Sequence[],
  alignedSequences: SequenceAlign[],
  selection: SequenceSelection,
  featureDefinitions: FeatureDefinitions,
  labelComposition: string[],
) {
  const str = (selection.ids.length === 0 ? alignedSequences : selectedAlignedSequences(alignedSequences, selection))
    .map((alignedSequence) => {
      const rawSequence = rawSequences.find((s) => s.id === alignedSequence.id) as Sequence;
      const sequence = sequences.find((s) => s.id === alignedSequence.id) as Sequence;

      const adjustedSelection = alignedSelectionToNonAlignedSelection(selection, alignedSequence);
      if (adjustedSelection && isSelectionGapsOnly(selection, [alignedSequence])) return undefined;

      // format: getSequenceLabel():FEATURE_TYPE:FEATURE_NAME (start-stop)
      return rawSequence.features
        .filter((feature) => featureDefinitions[buildFeatureId(feature)].visible)
        .sort((f1, f2) => {
          if (f1.start === f2.start) {
            // Then by descending length of the residues of that feature
            return f2.stop - f2.start - (f1.stop - f1.start);
          }
          // First sort by start position
          return f1.start - f2.start;
        })
        .map((feature) => {
          // Find if the selection is intersecting the feature.
          // Features intersection partially are part of the selection.
          if (adjustedSelection) {
            const intersection = findRangeIntersection(
              [feature.start - 1, feature.stop - 1],
              [adjustedSelection.selectionPositionStart, adjustedSelection.selectionPositionEnd],
            );
            if (!intersection) return undefined;
          }

          const alignedFeature = sequence.features.find((f) => f.id === feature.id);
          if (!alignedFeature) return undefined;

          return [
            [
              ">",
              getSequenceLabel(rawSequence, labelComposition, true),
              `:${feature.type}:${buildFeatureName(feature)}`,
              content === "aligned"
                ? ` (${alignedFeature.start + 1}-${alignedFeature.stop + 1})` // show aligned position
                : ` (${feature.start}-${feature.stop})`, // show un-aligned (raw) position
            ].join(""),
            content === "aligned"
              ? alignedSequence.residues.slice(alignedFeature.start, alignedFeature.stop + 1) // show aligned feature residues
              : sequence.residues.slice(feature.start - 1, feature.stop), // show un-aligned (raw) feature residues
          ].join("\n");
        })
        .filter((v) => v)
        .join("\n");
    })
    .filter((v) => v)
    .join("\n");

  return str;
}

function hasSequenceRowsIdenticalFeatures(rows: SequenceRow[]) {
  if (rows.length === 1) return false;
  const features = arrayFlat(rows.map((row) => row.features.map((f) => f.feature.name)));
  return features.length !== arrayUnique(features).length;
}

export function ensureUniqueFeatureNameByAddingIndex(sequences: Sequence[]) {
  return sequences.map((s) => {
    const features = s.features.map((f) => {
      let index: number | undefined;
      const sameNameFeatures = s.features
        .filter((ff) => ff.name === f.name || ff.type === f.type)
        .sort((a, b) => a.start - b.start);
      if (sameNameFeatures.length > 1) index = sameNameFeatures.findIndex((ff) => ff === f);
      return { ...f, name: index === undefined ? (f.name ?? f.type) : `${f.name ?? f.type} (${index + 1})` };
    });

    return { ...s, features };
  });
}

interface SequenceRow {
  id: string;
  name: string;
  residues: string;
  chainInfoLabel: string;
  features: { feature: SequenceFeature; residues: string }[];
}

export function sequenceFeaturesSelectionAsCSV(
  fileFormat: "csv" | "tsv",
  content: "aligned" | "raw",
  selectedFeatures: string[],
  rawSequencesProp: Sequence[],
  sequences: Sequence[],
  alignedSequences: SequenceAlign[],
  selection: SequenceSelection,
  featureDefinitions: FeatureDefinitions,
  includeFullSequence: boolean,
) {
  const rawSequences = ensureUniqueFeatureNameByAddingIndex(rawSequencesProp);

  const separator = fileFormat === "csv" ? "," : "\t";
  const rows: SequenceRow[] = (
    selection.ids.length === 0 ? alignedSequences : selectedAlignedSequences(alignedSequences, selection)
  )
    .map((alignedSequence) => {
      const rawSequence = rawSequences.find((s) => s.id === alignedSequence.id) as Sequence;
      const sequence = sequences.find((s) => s.id === alignedSequence.id) as Sequence;

      const adjustedSelection = alignedSelectionToNonAlignedSelection(selection, alignedSequence);
      if (adjustedSelection && isSelectionGapsOnly(selection, [alignedSequence])) {
        return {
          id: "",
          name: "",
          residues: "",
          chainInfoLabel: "",
          features: [],
        };
      }

      const row: SequenceRow = {
        id: getSequenceLabel(rawSequence, [LABEL_COMPONENT.ENTITY_QUALIFIED_ID], true),
        name: getSequenceLabel(rawSequence, [LABEL_COMPONENT.ENTITY_LABEL], true),
        residues: content === "aligned" ? alignedSequence.residues : sequence.residues,
        chainInfoLabel: rawSequence.chainInfoLabel || "CHAIN",

        features: [],
      };

      rawSequence.features
        .filter((feature) => {
          const featureNameWithIndex = feature.name?.match(/^([^.]+) \((\d+)\)/);
          // Remove index from feature name when trying to find the associated feature definition
          const name = featureNameWithIndex ? featureNameWithIndex[1] : (feature.name ?? feature.type);
          return featureDefinitions[buildFeatureId({ ...feature, name })].visible;
        })
        .filter((feature) => feature.type.match(new RegExp(`^(${selectedFeatures.join("|")})$`)) !== null)

        .forEach((feature) => {
          const alignedFeature = sequence.features.find((f) => f.id === feature.id);
          if (!alignedFeature) return undefined;

          const residues =
            content === "aligned"
              ? alignedSequence.residues.slice(alignedFeature.start, alignedFeature.stop + 1) // show aligned feature residues
              : sequence.residues.slice(feature.start - 1, feature.stop); // show un-aligned (raw) feature residues

          row.features.push({ feature, residues });
        });

      return row;
    })
    .filter((row) => row.features.length > 0);

  // For each feature pick the one with the lowest position to define the column order
  const featuresWithLowestStartPosition = arrayFlat(rows.map((r) => r.features.map((f) => f.feature))).reduce<
    Record<string, number>
  >((acc, f) => {
    if (acc[f.name || ""]) {
      if (f.start < acc[f.name || ""]) {
        acc[f.name || ""] = f.start;
      }
    } else {
      acc[f.name || ""] = f.start;
    }
    return acc;
  }, {});

  // Sort features by position (asc)
  const orderedFeatures = Object.keys(featuresWithLowestStartPosition).sort(
    (a, b) => featuresWithLowestStartPosition[a] - featuresWithLowestStartPosition[b],
  );

  const chainInfoLabels = arrayUnique(rows.map((row) => row.chainInfoLabel));

  // Header contains sequence identifier, features and chain(s) label(s)
  let headersAsCSV = ["ID", "Name", ...orderedFeatures];
  if (includeFullSequence) headersAsCSV = [...headersAsCSV, ...chainInfoLabels];
  const headersAsCSVStr = headersAsCSV.join(separator);

  // Merge sequences so we have all chains in one line
  const mergedRows = rows.reduce<{ id: string; name: string; rows: SequenceRow[] }[]>((acc, r) => {
    let index = acc.findIndex((accRow) => accRow.id === r.id);

    // If some chains belonging to the same sequence have the same features,
    // we show one chain per line instead of all on the same line.
    if (hasSequenceRowsIdenticalFeatures(rows.filter((row) => row.id === r.id))) index = -1;

    if (index !== -1) {
      acc[index].name += `, ${r.name}`;
      acc[index].rows.push(r);
    } else {
      acc.push({ id: r.id, name: r.name, rows: [r] });
    }
    return acc;
  }, []);

  // Build content
  const contentAsCSVStr = mergedRows
    .map((row) => {
      const features = arrayFlat(row.rows.map((r) => r.features));

      let data = [
        // Unique identifier
        row.id,
        // Name
        row.name,
        // Features in order
        ...orderedFeatures.map((f) => features.find((rf) => rf.feature.name === f)?.residues || ""),
      ];

      if (includeFullSequence) {
        data = [
          ...data.map((v) => (v ? `"${v}"` : "")),
          // Chain(s) label(s)
          ...chainInfoLabels.map(
            (chainInfoLabel) => row.rows.find((r) => r.chainInfoLabel === chainInfoLabel)?.residues || "",
          ),
        ];
      }

      return data.join(separator);
    })
    .join("\n");

  return `${headersAsCSVStr}\n${contentAsCSVStr}`;
}

/**
 * Insert residues within an existing sequence at a given position
 */
export function insertGaps(residues: string, position: number, gapsToInsert: string) {
  const residuesAsArray = residuesStringToArray(residues);
  const extraResiduesAsArray = residuesStringToArray(gapsToInsert);
  return arrayInsert(residuesAsArray, extraResiduesAsArray, position).join("");
}

/**
 * Insert gaps in the selected sequences at a given position.
 * Adjust features position if they are impacted by the new gaps.
 */
export function insertGapsIntoSelectedSequences(
  selection: SequenceSelection,
  alignedSequences: SequenceAlign[],
  sequences: Sequence[],
  position: number,
  residuesToInsert: string,
) {
  const updatedAlignedSequences: SequenceAlign[] = [];
  const updatedSequences: Sequence[] = [];

  alignedSequences.forEach((alignedSequence) => {
    const isSelected = selection.ids.includes(alignedSequence.id);
    const sequence = sequences.find((s) => s.id === alignedSequence.id);
    if (!sequence) return;

    if (isSelected) {
      // SequenceAlign
      const updatedAlignedSequence: SequenceAlign = { ...alignedSequence };

      updatedAlignedSequence.residues = insertGaps(alignedSequence.residues, position, residuesToInsert);

      updatedAlignedSequences.push(updatedAlignedSequence);

      // Sequence
      const updatedSequence: Sequence = { ...sequence };

      updatedSequence.features = updatedSequence.features.map((feature) => {
        const updatedFeature: SequenceFeature = { ...feature };
        if (position < updatedFeature.start) updatedFeature.start += residuesToInsert.length;
        if (position <= updatedFeature.stop) updatedFeature.stop += residuesToInsert.length;
        return updatedFeature;
      });

      updatedSequences.push(updatedSequence);
    } else {
      updatedAlignedSequences.push(alignedSequence);
      updatedSequences.push(sequence);
    }
  });

  return { updatedAlignedSequences, updatedSequences };
}

export function createSequenceFromManualInsert(
  qualifiedId: string,
  name: string,
  residues: string,
  sequenceType: SequenceType,
) {
  const newSequence: Sequence = {
    qualifiedId,
    id: generateRandomNumber(),
    name,
    description: "",
    synonym: "",
    sequenceType,
    residues: removeAllGaps(residues),
    circular: false,
    features: [],
  };

  const newAlignedSequence: SequenceAlign = {
    id: newSequence.id,
    residues,
  };

  return { newSequence, newAlignedSequence };
}

export function createSequencesFromSequenceSelection(
  selection: SequenceSelectionWithSequenceData,
  removeGaps: boolean,
) {
  const mappingOldIdNewId: Record<number, number> = {};
  const newRawSequences: Sequence[] = [];

  const newSequences = selection.sequences.map((sequence) => {
    const uniqueId = generateRandomNumber();
    const uniqueQualifiedId = `
      ${SequenceQualifiedIdTypes.SessionStore}-${generateRandomNumber().toString().slice(0, 4)}
    `;
    mappingOldIdNewId[sequence.id] = uniqueId;

    const newSequence: Sequence = { ...sequence };
    const rawSequence = selection.rawSequences.find((s) => s.id === newSequence.id);
    const newRawSequence: Sequence | undefined = rawSequence ? { ...rawSequence } : undefined;

    if (newRawSequence) {
      // Generate a new unique ID
      newRawSequence.id = uniqueId;
      newSequence.id = newRawSequence.id;
      newRawSequence.qualifiedId = uniqueQualifiedId;
      newSequence.qualifiedId = newRawSequence.qualifiedId;

      // Get original features (not recalculated including gaps) when removing gaps
      newSequence.features = [...newRawSequence.features];

      // Remove features if the selection is partial
      if (selection.positionStart !== undefined && selection.positionEnd !== undefined) {
        newSequence.features = [];
        newRawSequence.features = [];
      }

      newRawSequences.push(newRawSequence);
    }

    return newSequence;
  });

  // Format residues if position is selected, also depends if insert row/aligned is set
  const newAlignedSequences = selection.alignedSequences.map((alignedSequence) => {
    const newAlignedSequence: SequenceAlign = { ...alignedSequence };

    // Update the unique ID to match the sequence ID set above
    newAlignedSequence.id = mappingOldIdNewId[alignedSequence.id];

    // Related sequence
    const newRawSequence = newRawSequences.find((s) => s.id === newAlignedSequence.id);

    const newSequence = newSequences.find((s) => s.id === newAlignedSequence.id);

    // Keep only partial selection of residues
    if (selection.positionStart !== undefined && selection.positionEnd !== undefined) {
      newAlignedSequence.residues = residuesSelection(
        newAlignedSequence.residues,
        selection.positionStart,
        selection.positionEnd,
      );
    }

    // Remove gaps
    if (removeGaps) {
      newAlignedSequence.residues = removeAllGaps(newAlignedSequence.residues);
    }

    // Update sequences residues as the user has selected from the aligned ones visually
    if (selection.positionStart !== undefined && selection.positionEnd !== undefined && newRawSequence && newSequence) {
      newRawSequence.residues = removeAllGaps(newAlignedSequence.residues);
      newSequence.residues = removeAllGaps(newAlignedSequence.residues);
    }

    return newAlignedSequence;
  });

  return { newRawSequences, newSequences, newAlignedSequences };
}

/**
 * Sort aligned sequences based on sequences composed names. Return in ascending order.
 */
export function sortAlignedSequences(
  alignedSequences: SequenceAlign[],
  sequences: Sequence[],
  labelComposition: string[],
) {
  return [...alignedSequences].sort((s1, s2) =>
    getSequenceLabel(sequences.find((s) => s.id === s1.id) as Sequence, labelComposition).localeCompare(
      getSequenceLabel(sequences.find((s) => s.id === s2.id) as Sequence, labelComposition),
    ),
  );
}
